import { supabase } from "@/integrations/supabase/client";

// Deep link types and interfaces
export interface DeepLinkParams {
  [key: string]: string | number | boolean | undefined;
}

export interface TimelineDeepLink {
  username: string;
  sectionId?: string;
  entryId?: string;
  action?: 'view' | 'add' | 'edit';
  date?: string;
}

export interface ProfileDeepLink {
  username?: string;
  profileId?: string;
  action?: 'manage' | 'create' | 'edit';
}

export interface ShareDeepLink {
  type: 'timeline' | 'memory';
  targetId: string;
  entryIds?: string[];
}

export interface ActionDeepLink {
  type: 'approve' | 'connect' | 'handover';
  targetId: string;
  token?: string;
}

// Base deep link generator
export const generateDeepLink = (
  basePath: string,
  params: DeepLinkParams = {},
  queryParams: DeepLinkParams = {}
): string => {
  const currentDomain = window.location.origin;
  
  // Build path with parameters
  let path = basePath;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      path = path.replace(`{${key}}`, String(value));
    }
  });
  
  // Build query string
  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return `${currentDomain}${path}${queryString ? `?${queryString}` : ''}`;
};

// Timeline deep links
export const generateTimelineDeepLink = (params: TimelineDeepLink): string => {
  const queryParams: DeepLinkParams = {};
  
  if (params.entryId) {
    queryParams.entry = params.entryId;
    if (params.sectionId) {
      queryParams.section = params.sectionId;
    }
  }
  
  if (params.sectionId && !params.entryId) {
    queryParams.section = params.sectionId;
    if (params.action === 'add') {
      queryParams.action = 'add';
      if (params.date) queryParams.date = params.date;
    }
  }
  
  return generateDeepLink('/timeline/{username}', {
    username: params.username
  }, queryParams);
};

// Profile deep links
export const generateProfileDeepLink = (params: ProfileDeepLink): string => {
  if (params.action === 'manage' && params.username) {
    return generateDeepLink('/profile/{username}/manage', {
      username: params.username
    });
  }
  
  if (params.action === 'create') {
    const queryParams: DeepLinkParams = {};
    if (params.profileId) queryParams.for = params.profileId;
    
    return generateDeepLink('/profile/create', {}, queryParams);
  }
  
  return generateDeepLink('/profile/{username}', {
    username: params.username || 'me'
  });
};

// Share deep links
export const generateShareDeepLink = (params: ShareDeepLink): string => {
  if (params.type === 'timeline') {
    const queryParams: DeepLinkParams = {};
    if (params.entryIds && params.entryIds.length > 0) {
      queryParams.entries = params.entryIds.join(',');
    }
    
    return generateDeepLink('/share/timeline/{targetId}', {
      targetId: params.targetId
    }, queryParams);
  }
  
  return generateDeepLink('/share/memory/{targetId}', {
    targetId: params.targetId
  });
};

// Action deep links
export const generateActionDeepLink = (params: ActionDeepLink): string => {
  const queryParams: DeepLinkParams = {};
  if (params.token) queryParams.token = params.token;
  
  switch (params.type) {
    case 'approve':
      return generateDeepLink('/approve/{targetId}', {
        targetId: params.targetId
      }, queryParams);
    
    case 'connect':
      return generateDeepLink('/connect/{targetId}', {
        targetId: params.targetId
      });
    
    case 'handover':
      return generateDeepLink('/handover/{targetId}', {
        targetId: params.targetId
      });
    
    default:
      return generateDeepLink('/');
  }
};

// Deep link validation and parsing
export const parseDeepLinkParams = (pathname: string, search: string) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const searchParams = new URLSearchParams(search);
  
  const queryParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  
  return {
    pathParts,
    queryParams,
    fullPath: pathname,
    search
  };
};

// Security validation for deep links
export const validateDeepLinkAccess = async (
  linkType: string,
  targetId: string,
  token?: string
): Promise<{ valid: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { valid: false, error: 'Authentication required' };
    }
    
    // Validate based on link type
    switch (linkType) {
      case 'timeline':
      case 'profile':
        // Check if user can access this profile/timeline
        const { data: canAccess } = await supabase.rpc('can_access_timeline_enhanced', {
          target_user_id: targetId
        });
        
        if (!canAccess) {
          return { valid: false, error: 'Access denied' };
        }
        break;
      
      case 'handover':
        if (!token) {
          return { valid: false, error: 'Token required for handover links' };
        }
        
        // Validate handover token
        const { data: tokenData } = await supabase
          .from('profile_handover_tokens')
          .select('*')
          .eq('token_code', token)
          .eq('is_used', false)
          .gt('expires_at', new Date().toISOString())
          .single();
        
        if (!tokenData) {
          return { valid: false, error: 'Invalid or expired token' };
        }
        break;
      
      case 'approve':
        // Validate entry approval permissions
        const { data: entry } = await supabase
          .from('profile_timeline_entries')
          .select('user_id, requires_approval')
          .eq('id', targetId)
          .single();
        
        if (!entry || entry.user_id !== user.id) {
          return { valid: false, error: 'Not authorized to approve this entry' };
        }
        break;
    }
    
    return { valid: true };
  } catch (error) {
    console.error('Deep link validation error:', error);
    return { valid: false, error: 'Validation failed' };
  }
};

// Generate shareable links with metadata
export const generateShareableLink = async (
  type: 'timeline' | 'memory',
  targetId: string,
  options: {
    title?: string;
    description?: string;
    includePreview?: boolean;
  } = {}
) => {
  const baseUrl = window.location.origin;
  const link = type === 'timeline' 
    ? generateShareDeepLink({ type: 'timeline', targetId })
    : generateShareDeepLink({ type: 'memory', targetId });
  
  // Add metadata for social sharing
  const metadata = {
    url: link,
    title: options.title || `Check out this ${type} on Uplivion`,
    description: options.description || `Shared ${type} from Uplivion`,
    type: 'website',
    site_name: 'Uplivion'
  };
  
  return {
    link,
    metadata,
    shortLink: link // Could integrate with URL shortener if needed
  };
};

// Analytics tracking for deep links
export const trackDeepLinkUsage = (
  linkType: string,
  action: string,
  metadata?: Record<string, any>
) => {
  // This could integrate with analytics service
  console.log('Deep link tracked:', {
    linkType,
    action,
    timestamp: new Date().toISOString(),
    metadata
  });
  
  // Could send to analytics service here
  // analytics.track('deep_link_used', { linkType, action, ...metadata });
};

// Utility to handle deep link navigation with proper state management
export const navigateWithDeepLink = (
  navigate: (path: string) => void,
  link: string,
  options: {
    preserveAuth?: boolean;
    trackUsage?: boolean;
    metadata?: Record<string, any>;
  } = {}
) => {
  if (options.trackUsage) {
    trackDeepLinkUsage('navigation', 'click', options.metadata);
  }
  
  // Extract path from full URL if needed
  const url = new URL(link);
  const path = url.pathname + url.search;
  
  navigate(path);
};